import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { releasePointerFocus, useHotkeys } from '@/shared/lib/hotkeys'
import { HotkeyHints } from '@/shared/ui/HotkeyHints'

const UTILITY = 'timer'
const DEFAULT_TIMER_SECONDS = 5 * 60
const MAX_TIMER_SECONDS = 99 * 60 * 60 + 59 * 60 + 59
const QUICK_ADD_MINUTES = [1, 5, 10] as const
const TICK_MS = 50

function toMs(hours: number, minutes: number, seconds: number): number {
  return ((hours * 60 + minutes) * 60 + seconds) * 1000
}

function toTimerParts(totalMs: number): { hours: number; minutes: number; seconds: number } {
  const totalSeconds = Math.min(MAX_TIMER_SECONDS, Math.max(0, Math.ceil(totalMs / 1000)))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return { hours, minutes, seconds }
}

function clampInput(value: string, max: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 0
  return Math.min(max, Math.max(0, Math.trunc(parsed)))
}

function formatTimerClock(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(totalMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [hours, minutes, seconds]
    .map((part) => part.toString().padStart(2, '0'))
    .join(':')
}

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext
}

function createAudioContext(): AudioContext {
  const AudioContextCtor = window.AudioContext ?? (window as AudioWindow).webkitAudioContext
  if (!AudioContextCtor) {
    throw new Error('audio_context_unavailable')
  }
  return new AudioContextCtor()
}

function playCompletionSound(context: AudioContext): void {
  const gain = context.createGain()
  gain.gain.setValueAtTime(0.0001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.25, context.currentTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.85)
  gain.connect(context.destination)

  for (const [index, frequency] of [880, 660, 880].entries()) {
    const oscillator = context.createOscillator()
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(frequency, context.currentTime)
    oscillator.connect(gain)
    oscillator.start(context.currentTime + index * 0.18)
    oscillator.stop(context.currentTime + index * 0.18 + 0.14)
  }
}

function NumberInput({
  id,
  label,
  value,
  max,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: number
  max: number
  disabled: boolean
  onChange: (value: number) => void
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        id={id}
        type="number"
        min={0}
        max={max}
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(clampInput(event.target.value, max))}
        className={cn(
          'mt-1 h-11 w-full rounded-md border border-input bg-card px-3 text-center font-mono text-xl font-semibold tabular-nums',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          disabled && 'pointer-events-none opacity-60',
        )}
      />
    </label>
  )
}

export function TimerPage() {
  const t = useT()
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(Math.floor(DEFAULT_TIMER_SECONDS / 60))
  const [seconds, setSeconds] = useState(DEFAULT_TIMER_SECONDS % 60)
  const [timerRemainingMs, setTimerRemainingMs] = useState(DEFAULT_TIMER_SECONDS * 1000)
  const [isRunning, setIsRunning] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const startedAtRef = useRef(0)
  const timerBaseMsRef = useRef(timerRemainingMs)
  const originalTitleRef = useRef<string | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)

  const timerDurationMs = toMs(hours, minutes, seconds)
  const isTimerEmpty = timerDurationMs <= 0 && timerRemainingMs <= 0

  useEffect(() => {
    originalTitleRef.current ??= document.title

    if (isRunning) {
      document.title = `${formatTimerClock(timerRemainingMs)} - ${t.timer.title}`
      return
    }

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current
    }
  }, [isRunning, t.timer.title, timerRemainingMs])

  useEffect(() => {
    return () => {
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        void audioContextRef.current.close()
      }
    }
  }, [])

  const finishTimer = useCallback(() => {
    setIsRunning(false)
    setTimerRemainingMs(0)
    setNotice(t.timer.doneNotice)
    toolSuccess(UTILITY, timerDurationMs, {
      target_seconds: Math.round(timerDurationMs / 1000),
    })

    try {
      const context = audioContextRef.current ?? createAudioContext()
      audioContextRef.current = context
      playCompletionSound(context)
    } catch {
      toolError(UTILITY, 'sound_failed')
    }
  }, [t.timer.doneNotice, timerDurationMs])

  useEffect(() => {
    if (!isRunning) return

    const id = window.setInterval(() => {
      const now = performance.now()
      const nextMs = Math.max(0, timerBaseMsRef.current - (now - startedAtRef.current))
      setTimerRemainingMs(nextMs)
      if (nextMs <= 0) {
        finishTimer()
      }
    }, TICK_MS)

    return () => window.clearInterval(id)
  }, [finishTimer, isRunning])

  function handleHoursChange(nextHours: number) {
    setHours(nextHours)
    setTimerRemainingMs(toMs(nextHours, minutes, seconds))
  }

  function handleMinutesChange(nextMinutes: number) {
    setMinutes(nextMinutes)
    setTimerRemainingMs(toMs(hours, nextMinutes, seconds))
  }

  function handleSecondsChange(nextSeconds: number) {
    setSeconds(nextSeconds)
    setTimerRemainingMs(toMs(hours, minutes, nextSeconds))
  }

  function applyTimerDuration(nextMs: number) {
    const cappedMs = Math.min(MAX_TIMER_SECONDS * 1000, Math.max(0, nextMs))
    const nextParts = toTimerParts(cappedMs)

    setHours(nextParts.hours)
    setMinutes(nextParts.minutes)
    setSeconds(nextParts.seconds)
    setTimerRemainingMs(cappedMs)
  }

  function handleQuickAdd(minutesToAdd: number) {
    const addedMs = minutesToAdd * 60 * 1000
    const now = performance.now()
    const currentRemainingMs = isRunning
      ? Math.max(0, timerBaseMsRef.current - (now - startedAtRef.current))
      : timerRemainingMs
    const nextRemainingMs = Math.min(MAX_TIMER_SECONDS * 1000, currentRemainingMs + addedMs)

    applyTimerDuration(nextRemainingMs)
    setNotice(null)

    if (isRunning) {
      startedAtRef.current = now
      timerBaseMsRef.current = nextRemainingMs
    }
  }

  function handleStart() {
    if (timerDurationMs <= 0) {
      setNotice(t.timer.invalidDuration)
      toolError(UTILITY, 'invalid_duration')
      return
    }

    const now = performance.now()
    try {
      const context = audioContextRef.current ?? createAudioContext()
      audioContextRef.current = context
      if (context.state === 'suspended') {
        void context.resume()
      }
    } catch {
      audioContextRef.current = null
    }

    startedAtRef.current = now
    const remaining = timerRemainingMs > 0 ? timerRemainingMs : timerDurationMs
    timerBaseMsRef.current = remaining
    setTimerRemainingMs(remaining)
    toolInvoke(UTILITY, {
      target_seconds: Math.round(timerDurationMs / 1000),
    })

    setNotice(null)
    setIsRunning(true)
  }

  function handlePause() {
    const now = performance.now()
    setTimerRemainingMs(Math.max(0, timerBaseMsRef.current - (now - startedAtRef.current)))
    setIsRunning(false)
  }

  function handleReset() {
    setIsRunning(false)
    setNotice(null)
    setTimerRemainingMs(timerDurationMs)
  }

  function handleToggle() {
    if (isRunning) handlePause()
    else handleStart()
  }

  useHotkeys({
    Space: () => {
      if (!isTimerEmpty) handleToggle()
    },
    KeyR: handleReset,
  })

  return (
    <div className="mx-auto max-w-4xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.timer.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.timer.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-amber-300"
        />
        <CardContent className="space-y-3 p-3 sm:space-y-4 sm:p-6">
          <div className="rounded-lg border bg-background/70 px-2 py-5 text-center text-foreground shadow-inner dark:bg-background/80 sm:py-8">
            <div className="whitespace-nowrap font-mono text-[clamp(2.75rem,14vw,5.5rem)] font-bold leading-none tabular-nums">
              {formatTimerClock(timerRemainingMs)}
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-2 sm:gap-3">
            <Button
              type="button"
              onClick={(event) => {
                releasePointerFocus(event)
                handleToggle()
              }}
              disabled={isTimerEmpty}
              className="h-12 text-base"
            >
              {isRunning ? t.timer.pause : t.timer.start}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={(event) => {
                releasePointerFocus(event)
                handleReset()
              }}
              className="h-12 min-w-24 text-base sm:min-w-28"
            >
              {t.timer.reset}
            </Button>
          </div>

          <HotkeyHints
            label={t.common.hotkeys.label}
            items={[
              { key: t.common.hotkeys.space, action: t.common.hotkeys.startPause },
              { key: 'R', action: t.common.hotkeys.reset },
            ]}
          />

          {notice && (
            <div
              role="status"
              className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground"
            >
              {notice}
            </div>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <NumberInput
                id="timer-hours"
                label={t.timer.hours}
                value={hours}
                max={99}
                disabled={isRunning}
                onChange={handleHoursChange}
              />
              <NumberInput
                id="timer-minutes"
                label={t.timer.minutes}
                value={minutes}
                max={59}
                disabled={isRunning}
                onChange={handleMinutesChange}
              />
              <NumberInput
                id="timer-seconds"
                label={t.timer.seconds}
                value={seconds}
                max={59}
                disabled={isRunning}
                onChange={handleSecondsChange}
              />
            </div>

            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                {t.timer.quickAddLabel}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {QUICK_ADD_MINUTES.map((item) => (
                  <Button
                    key={item}
                    type="button"
                    variant="outline"
                    onClick={(event) => {
                      releasePointerFocus(event)
                      handleQuickAdd(item)
                    }}
                    className="h-10 px-2 font-mono text-sm tabular-nums"
                  >
                    +{item} {t.timer.quickAddMinuteShort}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {t.timer.timerHint}
          </p>
        </CardContent>
      </Card>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.timer.faqTitle}</h2>
        <dl className="mt-4 space-y-4">
          {t.timer.faq.map((item) => (
            <div key={item.question}>
              <dt className="font-medium">{item.question}</dt>
              <dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
            </div>
          ))}
        </dl>
        <h2 className="mt-8 text-xl font-semibold tracking-normal">{t.timer.relatedTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {t.timer.related.map((item) => (
            <a key={item.href} href={item.href} className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent">
              <span className="text-sm font-medium">{item.title}</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.description}</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  )
}
